import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Language } from '../core/models/language.model';
import { asString } from '../core/utils/coerce';
import { AppUserProfileResponse } from '../models/auth.model';
import { UpdateUser, User } from '../models/user.model';
import { AppUserProfileService } from './app-user-profile.service';
import { AuthStore } from './auth-store.service';

const USER_KEY = 'kcms-loyalty-user';
const LANG_KEY = 'kcms-loyalty-lang';

@Injectable({ providedIn: 'root' })
export class UserService {
   private authStore = inject(AuthStore);
   private appUserProfileService = inject(AppUserProfileService);
   private user: User;
   private language: Language;

   constructor() {
      this.language = this.readLanguage();
      this.user = this.hydrateUser();
   }

   getUserInfo(): User {
      return this.user;
   }

   getLanguage(): Language {
      return this.language;
   }

   setLanguage(language: Language): void {
      this.language = language;
      this.user.Language = language;
      try {
         localStorage.setItem(LANG_KEY, JSON.stringify(language));
      } catch {
         /* ignore */
      }
      this.persistUser();
   }

   replaceAuthenticatedProfile(user: User): void {
      this.user = { ...user, Language: user.Language || this.language };
      this.language = this.user.Language;
      this.persistUser();
   }

   clearAuthenticatedProfile(): void {
      const preferences = this.user;
      this.user = new User();
      this.user.Language = preferences.Language || this.language;
      this.persistUser();
   }

   update(request: UpdateUser, publishChanges: boolean = true): void {
      this.user.Name = request.Name;
      this.user.CellPhone = request.CellPhone;
      this.user.DocumentNumber = request.DocumentNumber;
      this.user.Email = request.Email;
      if (request.Photo) {
         this.user.PhotoUrl = request.Photo;
      }
      if (publishChanges) {
         this.persistUser();
      }
   }

   updateProfile(request: UpdateUser): Observable<User> {
      if (!this.authStore.isAuthenticated) {
         this.update(request);
         return of(this.user);
      }

      const previousPhotoUrl = this.user.PhotoUrl;
      return this.appUserProfileService
         .update({
            Name: request.Name,
            CellPhone: request.CellPhone,
            DocumentNumber: request.DocumentNumber,
            BirthDate: this.user.BirthDate || undefined,
            Photo: request.Photo,
         })
         .pipe(
            tap((profile) => {
               this.applyProfile(profile);
               if (request.Photo) {
                  const returned = asString(profile.PhotoUrl);
                  if (!returned || returned === previousPhotoUrl || returned.startsWith('assets/')) {
                     this.user.PhotoUrl = request.Photo;
                     this.persistUser();
                  }
               }
               this.authStore.setProfile({ ...profile, PhotoUrl: this.user.PhotoUrl });
            }),
            map(() => this.user),
         );
   }

   private hydrateUser(): User {
      if (this.authStore.profile) {
         return this.mapProfile(this.authStore.profile);
      }

      return this.readUser();
   }

   private applyProfile(profile: AppUserProfileResponse): void {
      this.user = this.mapProfile(profile);
      this.persistUser();
   }

   private mapProfile(profile: AppUserProfileResponse): User {
      const user = new User();
      if (!profile) {
         return user;
      }

      user.Id = asString(profile.Id);
      user.Name = asString(profile.Name);
      user.Email = asString(profile.Email);
      user.CellPhone = asString(profile.CellPhone);
      user.DocumentNumber = asString(profile.DocumentNumber);
      user.BirthDate = asString(profile.BirthDate);
      user.PhotoUrl = asString(profile.PhotoUrl, user.PhotoUrl);
      user.MemberSince = asString(profile.MemberSince);
      user.Language = this.language;
      return user;
   }

   private readUser(): User {
      try {
         const raw = localStorage.getItem(USER_KEY);
         if (raw) {
            const parsed = JSON.parse(raw) as Partial<User>;
            const user = new User();
            user.Id = asString(parsed.Id);
            user.Name = asString(parsed.Name);
            user.Email = asString(parsed.Email);
            user.CellPhone = asString(parsed.CellPhone);
            user.DocumentNumber = asString(parsed.DocumentNumber);
            user.BirthDate = asString(parsed.BirthDate);
            user.PhotoUrl = asString(parsed.PhotoUrl, user.PhotoUrl);
            user.MemberSince = asString(parsed.MemberSince);
            user.Language = this.language;
            return user;
         }
      } catch {
         /* ignore */
      }
      return new User();
   }

   private persistUser(): void {
      try {
         localStorage.setItem(USER_KEY, JSON.stringify(this.user));
      } catch {
         /* ignore quota / private mode */
      }
   }

   private readLanguage(): Language {
      try {
         const raw = localStorage.getItem(LANG_KEY);
         if (raw) {
            return { ...new Language(), ...JSON.parse(raw) };
         }
      } catch {
         /* ignore */
      }
      return new Language();
   }
}
